import { useEffect } from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  {
    id: "intro",
    title: "1. Introduction",
    body: [
      "PhysioSewa (\"we\", \"us\", \"our\") operates the website physiosewa.in and the physiotherapy clinic located at B-71, 40 Feet Road, New Palam Vihar Phase 1, Sector 110, Gurugram, Haryana 122017.",
      "This Privacy Policy explains how we collect, use, store and protect personal information when you visit our website, book an appointment, or receive treatment from us. By using our website or services, you consent to the practices described here.",
    ],
  },
  {
    id: "what-we-collect",
    title: "2. Information We Collect",
    body: [
      "We only collect information that is necessary to deliver our physiotherapy services and respond to your enquiries. Specifically:",
    ],
    list: [
      { strong: "Contact details", rest: " — full name, mobile number, email address." },
      { strong: "Appointment details", rest: " — service requested, visit type (clinic / home), preferred date and time, any notes you choose to share." },
      { strong: "Clinical information (in clinic only)", rest: " — relevant medical history, diagnosis, treatment plan and progress notes recorded during sessions." },
      { strong: "Technical data", rest: " — anonymous browser/device information and basic analytics (e.g. page views) for website performance." },
    ],
  },
  {
    id: "how-we-use",
    title: "3. How We Use Your Information",
    body: ["We use your information for the following purposes only:"],
    list: [
      { strong: "Booking & confirmation", rest: " — to confirm appointment slots, send WhatsApp / SMS / email confirmations, and reschedule when necessary." },
      { strong: "Treatment delivery", rest: " — to design a personalised physiotherapy plan and track progress safely." },
      { strong: "Service communication", rest: " — to respond to your queries by phone, WhatsApp or email." },
      { strong: "Quality & safety", rest: " — to maintain accurate clinical records as required by professional standards." },
    ],
  },
  {
    id: "sharing",
    title: "4. How We Share Your Information",
    body: [
      "We do not sell, rent or trade your personal information. We share information only in the limited circumstances below:",
    ],
    list: [
      { strong: "Treating physiotherapist", rest: " — your details are accessible to the physiotherapist assigned to your case." },
      { strong: "Referring doctors / surgeons", rest: " — only with your explicit consent, to coordinate your recovery." },
      { strong: "Service providers", rest: " — secure infrastructure (database hosting, messaging gateways) under strict confidentiality." },
      { strong: "Legal obligations", rest: " — when required by Indian law, court order, or to protect rights and safety." },
    ],
  },
  {
    id: "rights",
    title: "5. Your Rights",
    body: ["You have the right to:"],
    list: [
      { strong: "Access", rest: " — request a copy of the personal data we hold about you." },
      { strong: "Correction", rest: " — ask us to fix anything that is inaccurate or out of date." },
      { strong: "Deletion", rest: " — request deletion of your data, subject to clinical record-keeping requirements." },
      { strong: "Withdrawal", rest: " — withdraw consent for non-essential communications at any time." },
    ],
  },
  {
    id: "cookies",
    title: "6. Cookies & Analytics",
    body: [
      "Our website uses minimal essential cookies to keep the site functional and basic anonymous analytics to understand which pages are most useful to visitors. We do not use advertising trackers and we do not sell visitor data.",
      "You can disable cookies in your browser settings; the website will continue to work normally.",
    ],
  },
  {
    id: "security",
    title: "7. Data Security",
    body: [
      "We take reasonable technical and organisational measures to protect your information — including HTTPS/TLS encryption in transit, restricted database access, and access controls limited to authorised PhysioSewa staff. While no system can be 100% secure, we work continuously to safeguard your privacy.",
    ],
  },
  {
    id: "retention",
    title: "8. Data Retention",
    body: [
      "Appointment booking data is retained for as long as you remain an active patient and for a reasonable period thereafter, to support continuity of care. Clinical records are retained as per professional and regulatory guidelines applicable to physiotherapy practice in India.",
    ],
  },
  {
    id: "contact",
    title: "9. Contact Us",
    body: [
      "Questions, concerns or requests about this Privacy Policy or your personal data can be sent to us at:",
    ],
    contact: true,
  },
  {
    id: "changes",
    title: "10. Changes to this Policy",
    body: [
      "We may update this Privacy Policy from time to time to reflect changes in practice, technology or law. The latest version will always be published on this page with an updated effective date below.",
    ],
  },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App" data-testid="privacy-page" style={{ background: "white" }}>
      {/* HERO */}
      <section
        className="pt-[120px] pb-14 md:pb-20"
        style={{
          background: "linear-gradient(135deg, #F0FAF4 0%, #FFFFFF 70%)",
          borderBottom: "1px solid rgba(168,213,181,0.45)",
        }}
        data-testid="privacy-hero"
      >
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="mb-5">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold"
              style={{ color: "var(--primary)" }}
              data-testid="privacy-back-top"
            >
              <i className="fa-solid fa-arrow-left"></i> Back to Home
            </Link>
          </div>
          <span className="section-label" data-testid="privacy-label">Legal</span>
          <h1 className="heading-xl mt-1" style={{ fontSize: "clamp(2.1rem, 4.4vw, 3.2rem)" }}>
            Privacy Policy
          </h1>
          <p className="mt-4 text-base md:text-lg" style={{ color: "var(--text-mid)", lineHeight: 1.65 }}>
            Your trust matters to us. This page explains exactly what information PhysioSewa collects,
            how we use it, and the rights you have over your data.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 text-xs font-semibold"
               style={{ color: "var(--primary)", letterSpacing: "0.06em" }}>
            <i className="fa-solid fa-clock"></i>
            EFFECTIVE DATE: 1 JANUARY 2025
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-14 md:py-20" data-testid="privacy-content">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          {/* Sticky table of contents */}
          <aside className="lg:col-span-4">
            <div
              className="lg:sticky rounded-2xl p-5"
              style={{
                top: "100px",
                background: "var(--primary-pale)",
                border: "1px solid rgba(168,213,181,0.55)",
              }}
              data-testid="privacy-toc"
            >
              <div className="text-sm font-semibold mb-3" style={{ color: "var(--primary)", letterSpacing: "0.06em", textTransform: "uppercase" }}>
                On this page
              </div>
              <ol className="space-y-2 text-sm" style={{ color: "var(--text-mid)" }}>
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="hover:underline"
                      style={{ color: "var(--text-dark)", textDecoration: "none" }}
                      data-testid={`toc-${s.id}`}
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Body */}
          <article className="lg:col-span-8 space-y-10" data-testid="privacy-body">
            {SECTIONS.map((s) => (
              <section key={s.id} id={s.id} data-testid={`section-${s.id}`}>
                <h2
                  className="heading-lg"
                  style={{ fontSize: "clamp(1.4rem, 2.4vw, 1.7rem)", color: "var(--text-dark)" }}
                >
                  {s.title}
                </h2>
                {s.body && (
                  <div className="mt-3 space-y-3" style={{ color: "var(--text-mid)", lineHeight: 1.75 }}>
                    {s.body.map((p, i) => (<p key={i}>{p}</p>))}
                  </div>
                )}
                {s.list && (
                  <ul className="mt-4 space-y-2.5" style={{ color: "var(--text-mid)", lineHeight: 1.7 }}>
                    {s.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <i className="fa-solid fa-circle-check mt-1.5" style={{ color: "var(--primary)" }}></i>
                        <span>
                          <strong style={{ color: "var(--text-dark)", fontWeight: 600 }}>{item.strong}</strong>
                          {item.rest}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.contact && (
                  <div
                    className="mt-5 rounded-2xl p-5"
                    style={{
                      background: "var(--primary-pale)",
                      border: "1px solid rgba(168,213,181,0.55)",
                    }}
                    data-testid="privacy-contact-card"
                  >
                    <ul className="space-y-2 text-sm" style={{ color: "var(--text-dark)" }}>
                      <li><i className="fa-solid fa-envelope mr-2" style={{ color: "var(--primary)" }}></i>
                        <a href="mailto:info@physiosewa.in" style={{ color: "var(--text-dark)", fontWeight: 600 }}>info@physiosewa.in</a>
                      </li>
                      <li><i className="fa-solid fa-phone mr-2" style={{ color: "var(--primary)" }}></i>
                        <a href="tel:+919871053777" style={{ color: "var(--text-dark)", fontWeight: 600 }}>+91 98710 53777</a>
                      </li>
                      <li><i className="fa-solid fa-location-dot mr-2 mt-1" style={{ color: "var(--primary)" }}></i>
                        B-71, 40 Feet Road, New Palam Vihar Phase 1, Sector 110, Gurugram, Haryana 122017
                      </li>
                    </ul>
                  </div>
                )}
              </section>
            ))}

            <div
              className="rounded-2xl p-6 mt-8"
              style={{
                background: "linear-gradient(135deg, #6DBF8A, #A8D5B5)",
                color: "white",
              }}
              data-testid="privacy-bottom-cta"
            >
              <div className="flex items-start gap-4">
                <i className="fa-solid fa-shield-halved text-2xl mt-1"></i>
                <div>
                  <div className="text-lg font-bold">Have a question about your data?</div>
                  <p className="text-sm mt-1" style={{ color: "rgba(255,255,255,0.95)", lineHeight: 1.6 }}>
                    We respond to every privacy request within 7 working days. Just call, WhatsApp or email us.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2.5">
                    <a
                      href="https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20have%20a%20privacy%20question"
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
                      style={{ background: "white", color: "var(--primary)" }}
                      data-testid="privacy-whatsapp"
                    >
                      <i className="fa-brands fa-whatsapp" style={{ color: "#25D366" }}></i> WhatsApp Us
                    </a>
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold"
                      style={{ background: "transparent", color: "white", border: "2px solid rgba(255,255,255,0.6)" }}
                      data-testid="privacy-back-bottom"
                    >
                      <i className="fa-solid fa-arrow-left"></i> Back to Home
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
